import {createCard, makeCardSelectable} from "../_resources/ui-library.js";
import { createPageFromHomepage, checkSupaAccess } from "../_resources/supa-helpers.js";
import { resolveImageUrl } from "../_resources/utils.pages.js";
import { accessControlDialogCreate } from "../_resources/accesscontrol-dialog.js";


const PagesStatusSectionView = function({ dom, message, refresh, getToken, getUserId }) {
  const {bad, info, progress, good} = message;
  const container = dom;

  const sectionHeader = container.append("div").class("admin-section-header")
  const sectionHeaderLeft = sectionHeader.append("div").style("display", "flex").style("align-items", "center").style("gap", "12px");
  const h1 = sectionHeaderLeft.append("h1").text("Pages");

  const siteFilter = sectionHeaderLeft.append("select").class("input site-filter")
    .on("change", () => {
      renderCards();
      const selectedSite = siteFilter.property("value");
      const firstPage = _allData.find(d => d.site === selectedSite);
      if (firstPage && _onClickCard) {
        statusGrid.select("#card-" + firstPage.id).classed("is-selected", true);
        _onClickCard(firstPage);
      }
    });

  const actionsBox = sectionHeader.append("div").class("actionsbox").style("visibility", "hidden");
  const addNewButtonEl = actionsBox.append("button").class("button addnew").text("✚ New page");
  actionsBox.append("button").class("button").text("⬇⬆ Refresh").on("click", refresh)
  const formEl = actionsBox.append("form").class("form add-page-form").style("display", "none");

  const statusGrid = container.append("div").class("admin-grid small");

  let _allData = [];
  let _onClickCard = null;


  function render(data, onClickCard, selectedPage){
    _allData = data;
    _onClickCard = onClickCard;
    actionsBox.style("visibility", "visible");
    addNewButtonEl.on("click", () => renderAddForm());

    // Populate site filter dropdown, preserve previous selection if still valid
    const uniqueSites = [...new Set(data.map(p => p.site))];
    const multiSite = uniqueSites.length > 1;
    h1.text(multiSite ? "Pages at" : "Pages");
    siteFilter.style("display", multiSite ? null : "none");

    const prevSite = siteFilter.property("value");
    siteFilter.selectAll("option").remove();
    uniqueSites.forEach(site => {
      siteFilter.append("option").attr("value", site).text(site);
    });
    const initialSite = prevSite || selectedPage?.site;
    if (initialSite && uniqueSites.includes(initialSite)) {
      siteFilter.property("value", initialSite);
    }

    renderCards();
  }

  function renderCards() {
    const selectedSite = siteFilter.property("value");
    const filtered = _allData.filter(d => d.site === selectedSite);

    statusGrid.html(""); //clear previous cards
    const cards = statusGrid.selectAll("div.admin-card").data(filtered, d => d.id);
    cards.enter().append((d) => createCard({
      id: "card-" + d.id,
      title: d.slug === "__home__" ? "HOME" : "/" + d.slug,
      subtitle: `<a class="noarrow" href="${d.base_url + (d.slug === "__home__" ? "" : d.slug)}" target="_blank">↗</a>`,
      note: "",
      image: "./assets/images/page.png",
      faviconURL: d.theme_meta?.favicon ? resolveImageUrl(d.theme_meta.favicon, d.base_url) : null
    }));

    makeCardSelectable(statusGrid, (id) => {
      const selectedPage = filtered.find(d => "card-" + d.id === id);
      if (!selectedPage) return console.error("Can't select page", id );
      _onClickCard(selectedPage);
    });
  }

  function updateCard(page, selectedPageId, getToken, getUserId){
    onFormDestroy();
    const view = container.selectAll("#card-" + page.id);
    view.classed("is-selected", () => page.id === selectedPageId);
    
    // Add access control button for home pages
    if (page.slug === "__home__") {
      const extras = view.select(".card-extras");
      extras.text("");
      extras.append(() => getAccessControlButton(page.site, getToken, getUserId));
    }
  }

  function getAccessControlButton(site, getToken, getUserId) {
    const hasSiteOwnerAccess = checkSupaAccess({user: getUserId(), scope: "site", resource: site, atLeast: "owner"});
    const button = d3.create("button")
      .class("button")
      .style("display", hasSiteOwnerAccess ? null : "none")
      .text("Edit access")
      .on("click", async () => {
        await accessControlDialogCreate({ scope: "site", resource: site });
      })
    return button.node();
  }



  function renderAddForm() {
    onFormInit();
    
    const rowEl = formEl.append('div').class('form-row');

    rowEl.append('div').class('form-col')
      .call((group) => {
        group.append('label').for('slug').class('label required').text('slug');
        group.append('input')
          .type("text")
          .id("slug")
          .class('input')
          .placeholder('my-page')
          .required(true)
          .on("focus", () => info(`Short and readable identifier for the page URL. E.g. 'my-page' will result in 'https://domain.com/my-page'`))
          .on("blur", () => info());
      });
      rowEl.append('div').class('form-col').append('button')
        .type("submit")
        .class('button addnew')
        .text('Add this page');
      rowEl.append('div').class('form-col closecross')
        .text("✕").on("click", () => {
          onFormDestroy();
        })

      formEl.on('submit', async (e) => {
        e.preventDefault();

        const slug = formEl.select('#slug').property('value').trim();
        const site = siteFilter.property("value");
        const homepage = _allData.find(p => p.site === site && p.slug === "__home__");

        // Regex: lowercase letters a–z, digits, dash, underscore; not starting with digit or symbol
        const slugPattern = /^[a-z][a-z0-9_-]*$/;
        if (!slugPattern.test(slug)) 
          return bad(`Slug didn't pass the vibe check`); //❌

        if(_allData.find(page => page.slug === slug && page.site === site))
          return bad(`Slug ${slug} already taken on ${site} premise`); //❌

        await addPage(slug, site, homepage);
        onFormDestroy();
      });
  }



  function onFormInit(){
    actionsBox.selectAll("button").style("display", "none");
    formEl.style("display", null).html("");
  }

  function onFormDestroy(){
    actionsBox.selectAll("button").style("display", null);
    formEl.style("display", "none").html("");
  }



  async function addPage(slug, site, homepage) {
    const { data, error } = await createPageFromHomepage(slug, site, homepage);
    
    if (error) return bad('Error adding page: ' + JSON.stringify(error)); //❌
    
    const { page, translationsCopied, configsCopied, translationsError, configsError } = data;
    
    let message = `Page added successfully with id=${page.id}`;
    message += `, copied ${translationsCopied || "no"} translations`;
    if (translationsError) message += `⚠️ Translations: ${translationsError}`;
    message += ` and ${configsCopied || "no"} configs`;
    if (configsError) message += `⚠️ Configs: ${configsError}`;    
    
    good(message, 5000); //✅
    refresh();
  }





  return {render, updateCard};
};
export default PagesStatusSectionView;
